import { Alert, Modal, Upload, Button, message } from "antd";
import { UploadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { UploadFile } from "antd/es/upload/interface";
import apiClient from "../../../services/api/apiClient";

interface UploadAuditActModalProps {
  visible: boolean;
  onClose: () => void;
  auditId: number;
  onSuccess?: () => void;
  hasExistingAct?: boolean;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Modal for uploading the audit act PDF.
 *
 * Sends the PDF as `multipart/form-data` so Django streams it straight
 * to disk (no base64 +33% bloat, no `DATA_UPLOAD_MAX_MEMORY_SIZE` ceiling).
 */
export const UploadAuditActModal = ({
  visible,
  onClose,
  auditId,
  onSuccess,
  hasExistingAct = false,
}: UploadAuditActModalProps) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (fileList.length === 0) {
      message.error(
        t("audit.pdf_file_required") || "Please select a PDF file",
      );
      return;
    }

    const file = fileList[0].originFileObj as File;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const detail = t(
        "audit.pdf_too_large_detail",
        "El PDF pesa {{actual}} MB y el máximo permitido es {{max}} MB. Comprime el PDF (por ejemplo en ilovepdf.com/compress_pdf o en Adobe Acrobat → Comprimir PDF) y vuelve a subirlo.",
        {
          actual: (file.size / 1024 / 1024).toFixed(1),
          max: MAX_FILE_SIZE_MB,
        },
      );
      setSizeError(detail);
      message.error(detail);
      return;
    }

    setSizeError(null);
    setUploading(true);

    const formData = new FormData();
    formData.append("audit_act_file", file, file.name);

    try {
      await apiClient.post(
        `/audits/${auditId}/upload_audit_act/`,
        formData,
        {
          // Let axios derive the multipart boundary; explicitly
          // overriding the JSON default on the shared client.
          headers: { "Content-Type": "multipart/form-data" },
          // El timeout global son 30s; un PDF de 10MB en una
          // conexión 4G real puede tardar más. Subimos a 2 min.
          timeout: 120000,
        },
      );

      message.success(
        t("audit.upload_act_success") || "Audit act uploaded successfully",
      );
      setFileList([]);
      setUploading(false);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        t("audit.upload_act_error") ||
        "Failed to upload audit act";
      message.error(errorMessage);
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setSizeError(null);
    onClose();
  };

  // Custom upload validation
  const beforeUpload = (file: File) => {
    const isPDF =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPDF) {
      const msg =
        t("audit.invalid_pdf_file") || "El archivo debe ser un PDF (.pdf)";
      setSizeError(msg);
      message.error(msg);
      return Upload.LIST_IGNORE;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const detail = t(
        "audit.pdf_too_large_detail",
        "El PDF pesa {{actual}} MB y el máximo permitido es {{max}} MB. Comprime el PDF (por ejemplo en ilovepdf.com/compress_pdf o en Adobe Acrobat → Comprimir PDF) y vuelve a subirlo.",
        {
          actual: (file.size / 1024 / 1024).toFixed(1),
          max: MAX_FILE_SIZE_MB,
        },
      );
      setSizeError(detail);
      message.error(detail);
      return Upload.LIST_IGNORE;
    }

    setSizeError(null);
    return false; // Prevent auto upload
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    // Limit to 1 file
    newFileList = newFileList.slice(-1);
    setFileList(newFileList);
  };

  const modalTitle = hasExistingAct 
    ? (t("audit.replace_act") || "Reemplazar Acta de Auditoría")
    : (t("audit.upload_act") || "Subir Acta de Auditoría");

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText={hasExistingAct ? (t("common.replace") || "Reemplazar") : (t("common.upload") || "Subir")}
      cancelText={t("common.cancel") || "Cancelar"}
      confirmLoading={uploading}
      width={500}
    >
      <div style={{ padding: "20px 0" }}>
        {sizeError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setSizeError(null)}
            style={{ marginBottom: 12 }}
            message={t("audit.pdf_too_large_title", "Archivo demasiado grande")}
            description={sizeError}
          />
        )}
        <Upload
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleFileChange}
          maxCount={1}
          accept=".pdf,application/pdf"
          listType="text"
        >
          <Button
            icon={<UploadOutlined />}
            size="large"
            style={{ width: "100%" }}
          >
            <FilePdfOutlined style={{ marginLeft: 8 }} />
            {t("audit.select_pdf_file") || "Select PDF File"}
          </Button>
        </Upload>
        <div style={{ marginTop: 16, color: "#8c8c8c", fontSize: "12px" }}>
          {t(
            "audit.pdf_upload_hint",
            "Tamaño máximo: 10 MB. Solo se admiten archivos PDF. Si tu PDF supera 10 MB, compríme­lo en ilovepdf.com/compress_pdf o desde Adobe Acrobat (Archivo → Reducir tamaño del PDF).",
          )}
        </div>
      </div>
    </Modal>
  );
};
